import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi, ApiError } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { formatDate, formatDuration } from '@/lib/format';
import { absoluteUrl } from '@/lib/dom';
import { trackVideoPlay, trackVideoWatchTime } from '@/lib/analytics';
import { ShareBar } from '@/components/article';
import { ArticleImage } from '@/components/content';
import { VideoCard, VideoPlayer } from '@/components/video';
import { Badge, ErrorState } from '@/components/ui';
import { CardGridSkeleton, Section, Skeleton16x9 } from './page-parts';
import NotFound from './NotFound';

/**
 * `/:locale/videos/:slug` — PRD §13: player, title, description, byline, date, tags and
 * related videos. Every video is an iframe (ADR-006); nothing here is self-hosted.
 *
 * The page is server-rendered with its payload (§1.10) — a video's title and description
 * are exactly what a share preview reads, and the player before a click is an image and
 * a button, which needs no JavaScript to appear.
 */
export default function VideoPage() {
  const { locale, t, path } = useLocale();
  const slug = useParams<{ slug: string }>().slug ?? '';
  const videoPath = path('videos', slug);

  const {
    data: video,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.video(locale, slug),
    queryFn: () => publicApi.video(locale, slug),
    staleTime: STALE.article,
  });

  // Watch time is a stopwatch that reports on unmount, so the reader leaving is what
  // ends the measurement (see `analytics.ts` on why it is an upper bound).
  useEffect(() => {
    if (!video) return undefined;
    return trackVideoWatchTime(video.id, videoPath, video.durationSeconds);
  }, [video, videoPath]);

  if (isError) {
    // A slug nobody published is a real 404 — the same answer the server already gave
    // for this URL — not something to offer a retry button for.
    if (error instanceof ApiError && error.status === 404) return <NotFound />;
    return (
      <Section>
        <ErrorState
          title={t('error.genericTitle')}
          body={t('error.genericBody')}
          retryLabel={t('action.retry')}
          onRetry={() => void refetch()}
        />
      </Section>
    );
  }

  if (isPending || !video) {
    return (
      <>
        <Section>
          <Skeleton16x9 label={t('status.loading')} />
        </Section>
        <Section title={t('video.related')}>
          <CardGridSkeleton count={4} label={t('status.loading')} />
        </Section>
      </>
    );
  }

  return (
    <article>
      <Section className="pb-3">
        <VideoPlayer video={video} onPlay={() => trackVideoPlay(video.id, videoPath)} />
      </Section>

      <div className="mx-auto max-w-3xl px-gutter">
        <div className="flex flex-wrap items-center gap-2">
          {video.category && (
            <Link to={video.category.path}>
              <Badge tone="category" color={video.category.color}>
                {video.category.name}
              </Badge>
            </Link>
          )}
          {video.source === 'youtube_short' && <Badge tone="muted">{t('video.short')}</Badge>}
          {video.durationSeconds ? (
            <span className="text-xs text-muted">
              {formatDuration(video.durationSeconds, locale)}
            </span>
          ) : null}
          <span className="text-xs text-muted">{formatDate(video.publishedAt, locale)}</span>
        </div>

        <h1 className="mt-3 text-3xl font-extrabold text-ink">{video.title}</h1>

        {video.author && (
          <Link
            to={video.author.path}
            className="mt-4 flex items-center gap-2.5 border-y border-line py-3.5"
          >
            {video.author.photo ? (
              <ArticleImage
                image={video.author.photo}
                alt={video.author.name}
                sizes="36px"
                aspect="aspect-square"
                className="h-9 w-9 shrink-0 overflow-hidden rounded-full"
              />
            ) : null}
            <span className="text-sm font-bold text-ink">{video.author.name}</span>
          </Link>
        )}

        {video.description && (
          <p className="mt-5 whitespace-pre-line text-md leading-relaxed text-body">
            {video.description}
          </p>
        )}

        {/* The written version, when this clip belongs to a story. */}
        {video.article && (
          <Link
            to={video.article.path}
            className="mt-5 flex flex-col gap-1 rounded-lg border border-line p-4 transition hover:border-red"
          >
            <span className="text-xs font-bold uppercase tracking-wide text-muted">
              {t('video.fromStory')}
            </span>
            <span className="text-base font-bold text-ink">{video.article.headline}</span>
          </Link>
        )}

        {video.tags.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <li key={tag.id}>
                <Link
                  to={tag.path}
                  className="inline-block rounded-md border border-line px-3 py-1.5 text-xs font-semibold text-body transition hover:border-red hover:text-red"
                >
                  #{tag.name}
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 border-t border-line pt-5">
          <ShareBar url={absoluteUrl(video.path)} title={video.title} />
        </div>
      </div>

      {video.related.length > 0 && (
        <Section title={t('video.related')}>
          <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
            {video.related.map((related) => (
              <VideoCard key={related.id} video={related} />
            ))}
          </div>
        </Section>
      )}
    </article>
  );
}
